import { Button } from "@/components/ui/button";
import { Input, InputPassword } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SelectSearch } from "@/components/ui/select-search";
import { useServices } from "@/services/hooks/useServices";
import { CaretLeft } from "@phosphor-icons/react";
import { Link, useRouter } from "@tanstack/react-router";
import {
  useOrganizationList,
  useSession,
  useSignUp,
} from "@wacht/tanstack-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";

// Common country codes for phone input
const PHONE_COUNTRY_CODES = [
  { value: "+1", label: "+1 (US/CA)" },
  { value: "+44", label: "+44 (UK)" },
  { value: "+91", label: "+91 (India)" },
  { value: "+61", label: "+61 (Australia)" },
  { value: "+81", label: "+81 (Japan)" },
  { value: "+49", label: "+49 (Germany)" },
  { value: "+33", label: "+33 (France)" },
  { value: "+86", label: "+86 (China)" },
  { value: "+39", label: "+39 (Italy)" },
  { value: "+34", label: "+34 (Spain)" },
  { value: "+31", label: "+31 (Netherlands)" },
  { value: "+32", label: "+32 (Belgium)" },
  { value: "+55", label: "+55 (Brazil)" },
  { value: "+82", label: "+82 (South Korea)" },
  { value: "+65", label: "+65 (Singapore)" },
  { value: "+971", label: "+971 (UAE)" },
  { value: "+27", label: "+27 (South Africa)" },
  { value: "+52", label: "+52 (Mexico)" },
  { value: "+46", label: "+46 (Sweden)" },
  { value: "+47", label: "+47 (Norway)" },
  { value: "+358", label: "+358 (Finland)" },
  { value: "+45", label: "+45 (Denmark)" },
  { value: "+353", label: "+353 (Ireland)" },
  { value: "+64", label: "+64 (New Zealand)" },
  { value: "+48", label: "+48 (Poland)" },
  { value: "+90", label: "+90 (Turkey)" },
  { value: "+7", label: "+7 (Russia)" },
  { value: "+62", label: "+62 (Indonesia)" },
  { value: "+60", label: "+60 (Malaysia)" },
  { value: "+66", label: "+66 (Thailand)" },
  { value: "+63", label: "+63 (Philippines)" },
  { value: "+84", label: "+84 (Vietnam)" },
  { value: "+92", label: "+92 (Pakistan)" },
  { value: "+880", label: "+880 (Bangladesh)" },
  { value: "+94", label: "+94 (Sri Lanka)" },
  { value: "+20", label: "+20 (Egypt)" },
  { value: "+234", label: "+234 (Nigeria)" },
  { value: "+254", label: "+254 (Kenya)" },
  { value: "+233", label: "+233 (Ghana)" },
];

export function SignUpForm() {
  // Add maintenance mode state
  const [maintenanceMode] = useState(false); // Set to true to show maintenance message
  const router = useRouter();

  // Add countdown state
  const [timeRemaining, setTimeRemaining] = useState({
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  // const RESTRICTED_DOMAINS = [
  //   // Gmail
  //   "gmail.com",

  //   // Outlook / Microsoft
  //   "hotmail.com",
  //   "live.com",
  //   "msn.com",
  //   "outlook.com",
  //   "outlook.in",

  //   // Yahoo
  //   "rocketmail.com",
  //   "yahoo.co.in",
  //   "yahoo.com",
  //   "ymail.com",

  //   // AOL / Verizon
  //   "aol.com",
  //   "verizon.net",

  //   // Apple
  //   "icloud.com",
  //   "me.com",
  //   "mac.com",

  //   // ProtonMail
  //   "proton.me",
  //   "protonmail.ch",
  //   "protonmail.com",

  //   // Zoho
  //   "zoho.com",
  //   "zoho.eu",

  //   // GMX / Mail.com Group
  //   "email.com",
  //   "gmx.at",
  //   "gmx.com",
  //   "gmx.de",
  //   "gmx.fr",
  //   "gmx.net",
  //   "gmx.us",
  //   "mail.com",
  //   "usa.com",

  //   // Tutanota
  //   "keemail.me",
  //   "tuta.io",
  //   "tutamail.com",
  //   "tutanota.com",
  //   "tutanota.de",

  //   // Yandex
  //   "yandex.by",
  //   "yandex.com",
  //   "yandex.kz",
  //   "yandex.ru",
  //   "yandex.ua",

  //   // Mailinator & similar (temp)
  //   "maildrop.cc",
  //   "mailinator.com",
  //   "mailnesia.com",
  //   "moakt.com",
  //   "mytemp.email",
  //   "temp-mail.io",
  //   "temp-mail.org",
  //   "tempail.com",
  //   "tempmail.email",
  //   "tempmail.net",
  //   "tempmail.org",
  //   "throwawaymail.com",
  //   "trashmail.com",
  //   "trashmail.de",
  //   "10minutemail.com",
  //   "10minutemail.net",
  //   "getnada.com",
  //   "guerrillamail.com",
  //   "spam4.me",
  //   "spambog.com",
  //   "spambog.ru",
  //   "yopmail.com",
  //   "mail7.io",
  //   "anonaddy.com",
  //   "simplelogin.io",
  //   "dispostable.com",
  //   "fakeinbox.com",
  //   "emailondeck.com",
  //   "burnermail.io",
  //   "relay.firefox.com", // Firefox relay
  // ];

  const isRestrictedDomain = (email: string) => {
    const domain = email.split("@")[1]?.toLowerCase();
    return false;
    // return RESTRICTED_DOMAINS.includes(domain);
  };

  const [currentStep, setCurrentStep] = useState(1);
  const { getToken, refetch } = useSession();
  const { createOrganization } = useOrganizationList();
  const { organisationService, userService } = useServices();
  const { signUp, signupAttempt } = useSignUp();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    companyName: "",
    phoneNumber: "",
    phoneCountryCode: "+1",
  });
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [phoneOtpSent, setPhoneOtpSent] = useState(false);
  const [phoneOtpResendCooldown, setPhoneOtpResendCooldown] = useState(0);
  const [loading, setLoading] = useState(false);

  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    phoneNumber: "",
  });
  const [touched, setTouched] = useState({
    firstName: false,
    lastName: false,
    email: false,
    password: false,
    confirmPassword: false,
    phoneNumber: false,
  });
  const searchParams = new URLSearchParams(window.location.search);
  const clerkTicket = searchParams.get("__clerk_ticket");

  // Countdown timer effect
  useEffect(() => {
    if (!maintenanceMode) return;

    // Set target time to 7:00 PM today
    const calculateTimeRemaining = () => {
      const now = new Date();
      const targetTime = new Date(now);
      targetTime.setHours(19, 0, 0, 0); // 7:00 PM

      // If it's already past 7 PM, set target to tomorrow
      if (now > targetTime) {
        targetTime.setDate(targetTime.getDate() + 1);
      }

      const diffMs = targetTime.getTime() - now.getTime();
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      const diffSeconds = Math.floor((diffMs % (1000 * 60)) / 1000);

      return {
        hours: diffHours,
        minutes: diffMinutes,
        seconds: diffSeconds,
      };
    };

    // Initial calculation
    setTimeRemaining(calculateTimeRemaining());

    // Update countdown every second
    const timer = setInterval(() => {
      setTimeRemaining(calculateTimeRemaining());
    }, 1000);

    return () => clearInterval(timer);
  }, [maintenanceMode]);

  // Maintenance Message Component
  const MaintenanceMessage = () => (
    <div className="flex flex-col items-center justify-center h-full space-y-6 text-center">
      <div className="w-24 h-24 rounded-full bg-amber-100 flex items-center justify-center">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="48"
          height="48"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-amber-500"
        >
          <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
        </svg>
      </div>
      <h2 className="text-3xl font-bold">We're under maintenance</h2>
      <p className="text-muted-foreground max-w-md">
        We're making some improvements to our system. We'll be back online by
        7:00 PM.
      </p>

      {/* Countdown Timer */}
      <div className="mt-6">
        <p className="text-sm text-muted-foreground mb-3">
          Time until we're live:
        </p>
        <div className="flex gap-4 justify-center">
          <div className="flex flex-col items-center">
            <div className="bg-primary/10 rounded-lg w-16 h-16 flex items-center justify-center">
              <span className="text-2xl font-bold text-primary">
                {timeRemaining.hours.toString().padStart(2, "0")}
              </span>
            </div>
            <span className="text-xs mt-1 text-muted-foreground">Hours</span>
          </div>
          <div className="flex flex-col items-center">
            <div className="bg-primary/10 rounded-lg w-16 h-16 flex items-center justify-center">
              <span className="text-2xl font-bold text-primary">
                {timeRemaining.minutes.toString().padStart(2, "0")}
              </span>
            </div>
            <span className="text-xs mt-1 text-muted-foreground">Minutes</span>
          </div>
          <div className="flex flex-col items-center">
            <div className="bg-primary/10 rounded-lg w-16 h-16 flex items-center justify-center">
              <span className="text-2xl font-bold text-primary">
                {timeRemaining.seconds.toString().padStart(2, "0")}
              </span>
            </div>
            <span className="text-xs mt-1 text-muted-foreground">Seconds</span>
          </div>
        </div>
      </div>

      <p className="text-sm text-muted-foreground mt-4">
        Thank you for your patience.
      </p>
    </div>
  );

  const validateForm = () => {
    const newErrors = {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      confirmPassword: "",
      phoneNumber: "",
    };

    if (touched.firstName && formData.firstName.trim().length < 3) {
      newErrors.firstName = "First name must be at least 3 characters long";
    }

    if (touched.lastName && formData.lastName.trim().length < 3) {
      newErrors.lastName = "Last name must be at least 3 characters long";
    }

    if (touched.email && !clerkTicket) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = "Please enter a valid email address";
      }
    }

    if (touched.phoneNumber && formData.phoneNumber) {
      const digitsOnly = formData.phoneNumber.replace(/\D/g, "");
      if (digitsOnly.length < 7 || digitsOnly.length > 15) {
        newErrors.phoneNumber = "Phone number must contain 7–15 digits";
      }
    }

    if (touched.password) {
      if (formData.password.length < 8) {
        newErrors.password = "Password must be at least 8 characters long";
      } else if (!/[A-Z]/.test(formData.password)) {
        newErrors.password =
          "Password must contain at least one uppercase letter";
      } else if (!/[a-z]/.test(formData.password)) {
        newErrors.password =
          "Password must contain at least one lowercase letter";
      } else if (!/[0-9]/.test(formData.password)) {
        newErrors.password = "Password must contain at least one number";
      } else if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(formData.password)) {
        newErrors.password = "Password must contain at least one special character";
      }
    }

    if (
      touched.confirmPassword &&
      formData.confirmPassword !== formData.password
    ) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
  };

  useEffect(() => {
    validateForm();
  }, [
    formData.firstName,
    formData.lastName,
    formData.email,
    formData.password,
    formData.confirmPassword,
    formData.phoneNumber,
    touched.firstName,
    touched.lastName,
    touched.email,
    touched.password,
    touched.confirmPassword,
    touched.phoneNumber,
  ]);

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isRestrictedDomain(formData.email)) {
      toast.error("Please use a work email address");
      return;
    }

    if (Object.values(errors).some((error) => error !== "")) {
      toast.error("Please fix the errors before submitting");
      return;
    }

    setLoading(true);

    try {
      const phoneDigits = formData.phoneNumber.replace(/\D/g, "");
      const submitData: Record<string, string> = {
        email: formData.email,
        password: formData.password,
        first_name: formData.firstName,
        last_name: formData.lastName,
      };
      if (phoneDigits) {
        submitData.phone_number = phoneDigits;
        submitData.phone_country_code = formData.phoneCountryCode || "1";
      }
      const response = await signUp?.create(submitData);

      // Handle errors from response.errors array
      if (response?.errors && response.errors.length > 0) {
        const errorMessage = response.errors[0].message;
        // Check if it's a password-related error
        if (errorMessage.toLowerCase().includes("password")) {
          setErrors({
            ...errors,
            password: errorMessage,
          });
          // Mark password as touched so error shows
          setTouched((prev) => ({ ...prev, password: true }));
        } else {
          setErrors({
            ...errors,
            email: errorMessage,
          });
        }
        toast.error(errorMessage);
        setLoading(false);
        return;
      }

      // Handle errors from response.message (backend error format)
      if (response && 'message' in response && response.message) {
        const errorMessage = response.message as string;
        // Check if it's a password-related error
        if (errorMessage.toLowerCase().includes("password")) {
          setErrors({
            ...errors,
            password: errorMessage,
          });
          // Mark password as touched so error shows
          setTouched((prev) => ({ ...prev, password: true }));
        } else {
          setErrors({
            ...errors,
            email: errorMessage,
          });
        }
        toast.error(errorMessage);
        setLoading(false);
        return;
      }
    } catch (error: any) {
      // Handle any unexpected errors
      const errorMessage = error?.message || "An error occurred during sign up";
      toast.error(errorMessage);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!signupAttempt) return;

    if (signupAttempt.current_step === "verify_email" && !emailOtpSent) {
      setLoading(false);
      signUp.prepareVerification({ strategy: "email_otp" });
      setCurrentStep(2);
      setEmailOtpSent(true);
    }

    if (signupAttempt.current_step === "verify_phone" && !phoneOtpSent) {
      setLoading(false);
      signUp.prepareVerification({ strategy: "phone_otp" });
      setCurrentStep(3);
      setOtp(["", "", "", "", "", ""]);
      setPhoneOtpSent(true);
      setPhoneOtpResendCooldown(60);
    }

    if (!signupAttempt.completed) return;
    refetch().then(() => {
      router.navigate({ to: "/dashboard" });
    });
  }, [signupAttempt]);

  // Phone OTP resend cooldown timer
  useEffect(() => {
    if (currentStep !== 3 || phoneOtpResendCooldown <= 0) return;
    const timer = setInterval(() => {
      setPhoneOtpResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [currentStep, phoneOtpResendCooldown]);

  const handleResendPhoneOtp = async () => {
    if (phoneOtpResendCooldown > 0) return;
    setLoading(true);
    try {
      await signUp.prepareVerification({ strategy: "phone_otp" });
      setOtp(["", "", "", "", "", ""]);
      setPhoneOtpResendCooldown(60);
      toast.success("Verification code sent again");
      setTimeout(() => {
        const firstInput = document.getElementById("otp-input-0");
        if (firstInput) (firstInput as HTMLInputElement).focus();
      }, 100);
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Failed to resend code";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const clearOtpAndFocus = (firstInputId: string) => {
    setOtp(["", "", "", "", "", ""]);
    setTimeout(() => {
      const firstInput = document.getElementById(firstInputId);
      if (firstInput) (firstInput as HTMLInputElement).focus();
    }, 100);
  };

  const handleEmailVerification = async () => {
    setLoading(true);
    const otpCode = otp.join("");

    try {
      const response = await signUp.completeVerification(otpCode);

      // Handle errors from response.errors array
      if (response?.errors && response.errors.length > 0) {
        const errorMessage = response.errors[0].message;
        toast.error(errorMessage);
        clearOtpAndFocus("otp-input-0");
        setLoading(false);
        return;
      }

      // Handle errors from response.message (backend error format)
      if (response && "message" in response && response.message) {
        const errorMessage = response.message as string;
        toast.error(errorMessage);
        clearOtpAndFocus("otp-input-0");
        setLoading(false);
        return;
      }
    } catch (error: any) {
      const errorMessage =
        error?.message || "An error occurred during verification";
      toast.error(errorMessage);
      clearOtpAndFocus("otp-input-0");
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneVerification = async () => {
    setLoading(true);
    const otpCode = otp.join("");

    try {
      const response = await signUp.completeVerification(otpCode);

      if (response?.errors && response.errors.length > 0) {
        const errorMessage = response.errors[0].message;
        toast.error(errorMessage);
        clearOtpAndFocus("otp-input-0");
        return;
      }

      if (response && "message" in response && response.message) {
        const errorMessage = response.message as string;
        toast.error(errorMessage);
        clearOtpAndFocus("otp-input-0");
        return;
      }
    } catch (error: any) {
      const errorMessage =
        error?.message || "An error occurred during verification";
      toast.error(errorMessage);
      clearOtpAndFocus("otp-input-0");
    } finally {
      setLoading(false);
    }
  };

  const handleCompanySubmit = async () => {
    try {
      const token = await getToken("backend");
      setLoading(true);
      const organizations = await createOrganization!({
        name: formData.companyName,
      });

      await userService.onboardUser();
      // await organisationService.onboardOrganisation({
      //   organisationId: (organizations.data as any).organization.id,
      //   name: formData.companyName,
      // });

      router.navigate({ to: "/dashboard" });
    } catch (err) {
      toast.error("Failed to create organization");
      console.error(err, "failed org error");
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number,
  ) => {
    const value = e.target.value;
    if (/^[0-9]?$/.test(value)) {
      // Only allow numeric values
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      // Automatically focus next input field
      if (value && index < 5) {
        const nextInput = document.getElementById(`otp-input-${index + 1}`);
        if (nextInput) nextInput.focus();
      }
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    if (e.key === "Backspace" && !otp[index]) {
      // If current input is empty and backspace is pressed, focus previous input
      if (index > 0) {
        const prevInput = document.getElementById(`otp-input-${index - 1}`);
        if (prevInput) {
          prevInput.focus();
          // Clear the previous input value
          const newOtp = [...otp];
          newOtp[index - 1] = "";
          setOtp(newOtp);
        }
      }
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text/plain").replace(/\D/g, "");
    if (!pasted) return;
    const digits = pasted.slice(0, 6).split("");
    const newOtp: string[] = ["", "", "", "", "", ""];
    digits.forEach((d, i) => (newOtp[i] = d));
    setOtp(newOtp);
    const focusIndex = Math.min(digits.length, 5);
    setTimeout(() => {
      const input = document.getElementById(`otp-input-${focusIndex}`);
      if (input) (input as HTMLInputElement).focus();
    }, 0);
  };

  // Add handleBlur function to mark fields as touched
  const handleBlur = (field: keyof typeof touched) => {
    setTouched((prev) => ({
      ...prev,
      [field]: true,
    }));
  };

  return (
    <div className="w-full flex flex-col items-center justify-center">
      {/* Show maintenance message if in maintenance mode */}
      {maintenanceMode ? (
        <MaintenanceMessage />
      ) : (
        <>
          {currentStep === 1 ? (
            <>
              <div className="mb-8 text-center lg:text-left w-full">
                <h1 className="text-4xl font-semibold tracking-tight pb-14 text-center hidden lg:block">
                  Sign Up
                </h1>

                <h3 className="text-2xl font-semibold text-foreground mb-2">
                  Create your account
                </h3>
                <p className="text-sm text-muted-foreground">
                  Already have an account?{" "}
                  <Link to="/signin" className="text-primary hover:underline">
                    Sign in
                  </Link>
                </p>
              </div>

              <div className="space-y-4 w-full">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>First Name</Label>
                    <Input
                      placeholder="John"
                      value={formData.firstName}
                      onChange={(e) =>
                        setFormData({ ...formData, firstName: e.target.value })
                      }
                      onBlur={() => handleBlur("firstName")}
                      required
                    />
                    {errors.firstName && (
                      <p className="text-sm text-red-500 mt-1">
                        {errors.firstName}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label>Last Name</Label>
                    <Input
                      placeholder="Doe"
                      value={formData.lastName}
                      onChange={(e) =>
                        setFormData({ ...formData, lastName: e.target.value })
                      }
                      onBlur={() => handleBlur("lastName")}
                      required
                    />
                    {errors.lastName && (
                      <p className="text-sm text-red-500 mt-1">
                        {errors.lastName}
                      </p>
                    )}
                  </div>
                </div>

                {!clerkTicket && (
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      placeholder="john@example.com"
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                      onBlur={() => handleBlur("email")}
                      required
                    />
                    {errors.email && (
                      <p className="text-sm text-red-500 mt-1">
                        {errors.email}
                      </p>
                    )}
                  </div>
                )}

                <div>
                  <Label>Phone Number</Label>
                  <div className="flex gap-2">
                    <SelectSearch
                      options={PHONE_COUNTRY_CODES}
                      value={formData.phoneCountryCode}
                      onChange={(value) =>
                        setFormData({ ...formData, phoneCountryCode: value })
                      }
                      placeholder="Code"
                      triggerClassName="w-[130px] shrink-0"
                    />
                    <Input
                      type="tel"
                      placeholder="1234567890"
                      value={formData.phoneNumber}
                      onChange={(e) => {
                        const v = e.target.value.replace(/[^\d\s-]/g, "");
                        setFormData({ ...formData, phoneNumber: v });
                      }}
                      onBlur={() => handleBlur("phoneNumber")}
                      className="flex-1"
                      required
                    />
                  </div>
                  {errors.phoneNumber && (
                    <p className="text-sm text-red-500 mt-1">
                      {errors.phoneNumber}
                    </p>
                  )}
                </div>

                <div>
                  <Label>Password</Label>
                  <InputPassword
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={(e) =>
                      setFormData({ ...formData, password: e.target.value })
                    }
                    onBlur={() => handleBlur("password")}
                    required
                  />
                  {errors.password && (
                    <p className="text-sm text-red-500 mt-1">
                      {errors.password}
                    </p>
                  )}
                </div>

                <div>
                  <Label>Confirm Password</Label>
                  <InputPassword
                    placeholder="Confirm your password"
                    value={formData.confirmPassword}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        confirmPassword: e.target.value,
                      })
                    }
                    onBlur={() => handleBlur("confirmPassword")}
                    required
                  />
                  {errors.confirmPassword && (
                    <p className="text-sm text-red-500 mt-1">
                      {errors.confirmPassword}
                    </p>
                  )}
                </div>

                <Button
                  type="submit"
                  className="w-full bg-primary hover:bg-primary/80"
                  disabled={
                    loading ||
                    Object.values(errors).some((error) => error !== "") ||
                    !formData.firstName.trim() ||
                    !formData.lastName.trim() ||
                    (!clerkTicket && !formData.email.trim()) ||
                    !formData.phoneNumber.trim() ||
                    !formData.password ||
                    !formData.confirmPassword
                  }
                  onClick={handleSignUp}
                >
                  {loading ? "Creating Account..." : "Create Account"}
                </Button>
              </div>
            </>
          ) : currentStep === 2 ? (
            <div className="space-y-4">
              <div className="flex items-center mb-4">
                <Button
                  variant="ghost"
                  onClick={() => setCurrentStep(1)}
                  className="p-0 hover:bg-transparent"
                >
                  <CaretLeft />
                </Button>
                <h2 className="text-xl font-semibold ml-2">
                  Verify your email
                </h2>
              </div>

              <p className="text-gray-600">
                We've sent a verification code to {formData.email}
              </p>

              <div className="flex gap-2 justify-center">
                {otp.map((digit, index) => (
                  <Input
                    key={index}
                    id={`otp-input-${index}`}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={1}
                    className="w-12 h-12 text-center"
                    value={digit}
                    onChange={(e) => handleCodeChange(e, index)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    onPaste={handleOtpPaste}
                  />
                ))}
              </div>

              <Button
                onClick={handleEmailVerification}
                className="w-full bg-primary hover:bg-primary/80"
                disabled={loading || otp.some((digit) => !digit)}
              >
                {loading ? "Verifying..." : "Verify Email"}
              </Button>
            </div>
          ) : currentStep === 3 ? (
            <div className="space-y-4">
              <div className="flex items-center mb-4">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setCurrentStep(2);
                    setOtp(["", "", "", "", "", ""]);
                  }}
                  className="p-0 hover:bg-transparent"
                >
                  <CaretLeft />
                </Button>
                <h2 className="text-xl font-semibold ml-2">
                  Verify your phone
                </h2>
              </div>

              <p className="text-gray-600">
                We've sent a verification code to{" "}
                {formData.phoneCountryCode} {formData.phoneNumber.replace(/\D/g, "")}
              </p>

              <div className="flex gap-2 justify-center">
                {otp.map((digit, index) => (
                  <Input
                    key={index}
                    id={`otp-input-${index}`}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={1}
                    className="w-12 h-12 text-center"
                    value={digit}
                    onChange={(e) => handleCodeChange(e, index)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    onPaste={handleOtpPaste}
                  />
                ))}
              </div>

              <Button
                onClick={handlePhoneVerification}
                className="w-full bg-primary hover:bg-primary/80"
                disabled={loading || otp.some((digit) => !digit)}
              >
                {loading ? "Verifying..." : "Verify Phone"}
              </Button>

              <p className="text-center text-sm text-muted-foreground">
                Didn't receive the code?{" "}
                <button
                  type="button"
                  onClick={handleResendPhoneOtp}
                  disabled={loading || phoneOtpResendCooldown > 0}
                  className="text-primary font-medium hover:underline disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline"
                >
                  {phoneOtpResendCooldown > 0
                    ? `Resend in ${phoneOtpResendCooldown}s`
                    : "Resend OTP"}
                </button>
              </p>
            </div>
          ) : (
            <div className="space-y-4 w-full">
              <div className="flex items-center mb-4">
                <Button
                  variant="ghost"
                  onClick={() => setCurrentStep(3)}
                  className="p-0 hover:bg-transparent"
                >
                  <CaretLeft />
                </Button>
                <h2 className="text-xl font-semibold ml-2">
                  Enter Company Details
                </h2>
              </div>

              <div>
                <Label>Company Name</Label>
                <Input
                  type="text"
                  placeholder="Acme Inc."
                  value={formData.companyName}
                  onChange={(e) =>
                    setFormData({ ...formData, companyName: e.target.value })
                  }
                  required
                />
              </div>

              <Button
                onClick={handleCompanySubmit}
                className="w-full bg-primary hover:bg-primary/80"
                disabled={loading || !formData.companyName.trim()}
              >
                {loading ? "Creating Organization..." : "Complete Setup"}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
